import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useCarrierTestAutoReview } from "@/hooks/useCarrierTestAutoReview";
import useUrlState from "@/hooks/useUrlState";

interface CarrierTestItem {
  id: string;
  user_id: string;
  user_email: string;
  message: string;
  type: string;
  carriers: string[];
  recipients: { carrier: string; phone: string; name?: string }[];
  results: {
    carrier: string;
    phone: string;
    status: string;
    delivery_status?: string;
    message_id?: string;
    sent_at?: string;
  }[];
  review_results: {
    carrier: string;
    status: "success" | "spam";
    note?: string;
    reviewed_at?: string;
  }[];
  needs_review: boolean;
  reviewed_at?: string;
  created_at: string;
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

// 탭 복귀(리로드) 시 로딩 없이 마지막 데이터를 즉시 보여주기 위한 캐시
const ITEMS_CACHE_KEY = "cache:admin_carrier_test_review";

function readItemsCache(): CarrierTestItem[] | null {
  try {
    const raw = sessionStorage.getItem(ITEMS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CarrierTestItem[];
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeItemsCache(list: CarrierTestItem[]): void {
  try {
    sessionStorage.setItem(ITEMS_CACHE_KEY, JSON.stringify(list));
  } catch {
    // 캐시 저장 실패는 무시
  }
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}.${m}.${day} ${hh}:${mm}`;
}

const CARRIER_STYLES: Record<string, { border: string; bg: string; text: string }> = {
  SKT: { border: "border-red-500/30", bg: "bg-red-500/10", text: "text-red-400" },
  KT: { border: "border-sky-500/30", bg: "bg-sky-500/10", text: "text-sky-400" },
  "LGU+": { border: "border-pink-500/30", bg: "bg-pink-500/10", text: "text-pink-400" },
};

export default function AdminCarrierTestReviewTab() {
  const { session } = useAuth();
  const cachedItemsRef = useRef<CarrierTestItem[] | null>(readItemsCache());
  const [items, setItems] = useState<CarrierTestItem[]>(cachedItemsRef.current ?? []);
  const [loading, setLoading] = useState(cachedItemsRef.current === null);
  const [error, setError] = useState<string | null>(null);
  // 필터 상태를 주소(URL)에도 남겨 새로고침·링크 공유에도 유지 (sessionStorage 백업)
  const [filterRaw, setFilterRaw] = useUrlState("filter", "all");
  const filter: "all" | "pending" | "reviewed" =
    filterRaw === "pending" || filterRaw === "reviewed" ? filterRaw : "all";
  const setFilter = setFilterRaw;
  const [reviewing, setReviewing] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const fetchTests = useCallback(async () => {
    // 캐시된 데이터가 이미 있으면 로딩 화면 없이 조용히 갱신
    if (cachedItemsRef.current === null) setLoading(true);
    setError(null);
    try {
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/get-all-carrier-tests`, {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
          apikey: SUPABASE_ANON_KEY,
          "Content-Type": "application/json",
        },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: "Unknown error" }));
        throw new Error(err.error || `HTTP ${resp.status}`);
      }
      const data = await resp.json();
      const list = (data.tests || []) as CarrierTestItem[];
      setItems(list);
      writeItemsCache(list);
    } catch (err: any) {
      // 캐시된 데이터가 있으면 마지막 데이터를 유지하고 조용히 실패 처리
      if (cachedItemsRef.current === null) {
        setError(err.message || "데이터를 불러오는 중 오류가 발생했습니다.");
      }
    } finally {
      cachedItemsRef.current = cachedItemsRef.current ?? [];
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchTests();
  }, [fetchTests]);

  // Auto-review poller: runs every 30s when there are pending reviews
  const pendingCount = items.filter((i) => i.needs_review).length;
  const { triggerNow: triggerAutoReview } = useCarrierTestAutoReview(
    pendingCount > 0,
    fetchTests,
  );

  const handleRefresh = useCallback(async () => {
    await triggerAutoReview();
    await fetchTests();
  }, [triggerAutoReview, fetchTests]);

  const handleReview = async (testId: string, carrier: string, status: "success" | "spam") => {
    const key = `${testId}-${carrier}`;
    setReviewing((prev) => ({ ...prev, [key]: true }));
    try {
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/review-carrier-test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ test_id: testId, carrier, status, note: "" }),
      });
      const result = await resp.json();
      if (!resp.ok) throw new Error(result.error || "심사 저장 실패");
      showToast(`${carrier} → ${status === "success" ? "성공" : "스팸"}으로 판정 완료`);
      await fetchTests();
    } catch (err: any) {
      showToast(err.message || "심사 중 오류가 발생했습니다.");
    } finally {
      setReviewing((prev) => ({ ...prev, [key]: false }));
    }
  };

  const filtered = items.filter((item) => {
    if (filter === "pending") return item.needs_review;
    if (filter === "reviewed") return !item.needs_review;
    return true;
  });

  const reviewedCount = items.filter((i) => !i.needs_review).length;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-white font-semibold text-lg">3사 테스트 심사</h2>
          <p className="text-gray-500 text-sm mt-0.5">회원이 요청한 3사 테스트를 수신 결과 기준으로 판정합니다</p>
        </div>
        <button
          onClick={handleRefresh}
          className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/[0.05] border border-white/[0.08] text-gray-400 hover:text-white cursor-pointer transition-colors"
          title="새로고침 (자동 심사 포함)"
        >
          <i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`}></i>
        </button>
      </div>

      {/* Stats + Filter */}
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 bg-white/[0.03] border border-white/[0.08] rounded-lg p-1">
          {[
            { key: "all" as const, label: `전체 (${items.length})` },
            { key: "pending" as const, label: `심사 대기 (${pendingCount})` },
            { key: "reviewed" as const, label: `완료 (${reviewedCount})` },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer whitespace-nowrap transition-all ${
                filter === f.key
                  ? "bg-rose-600 text-white"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          <i className="ri-error-warning-line"></i>
          {error}
          <button
            onClick={fetchTests}
            className="ml-auto px-3 py-1 rounded-lg bg-red-500/15 text-red-400 text-xs hover:bg-red-500/25 cursor-pointer"
          >
            다시 시도
          </button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-rose-400 text-2xl mb-3"></i>
          <p className="text-gray-500 text-sm">불러오는 중...</p>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white/[0.04] mb-3">
            <i className="ri-smartphone-line text-xl text-gray-500"></i>
          </div>
          <p className="text-gray-400 text-sm font-medium">테스트 내역이 없습니다</p>
          <p className="text-gray-600 text-xs mt-1">
            {filter === "pending" ? "심사 대기 중인 테스트가 없습니다." : "해당 필터에 맞는 테스트가 없습니다."}
          </p>
        </div>
      )}

      {/* Cards */}
      {!loading && (
        <div className="space-y-3">
          {filtered.map((item) => {
            const recipientCarriers = [...new Set((item.recipients || []).map((r) => r.carrier).filter(Boolean))];
            // 카드에는 실제로 발송된 통신사만 표시
            const carriers = recipientCarriers;
            const reviewMap = new Map((item.review_results || []).map((r) => [r.carrier, r]));

            return (
              <div
                key={item.id}
                className={`rounded-xl border ${item.needs_review ? "border-amber-500/20 bg-amber-500/[0.03]" : "border-white/[0.06] bg-white/[0.03]"} p-4 space-y-3`}
              >
                {/* Top row */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-white text-sm font-medium">{item.user_email}</span>
                    <span className="text-gray-500 text-xs">{fmtDate(item.created_at)}</span>
                    {item.needs_review && (
                      <span className="px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-400 text-[10px] font-medium border border-amber-500/20 whitespace-nowrap">
                        심사 대기
                      </span>
                    )}
                    {!item.needs_review && (
                      <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 text-[10px] font-medium border border-emerald-500/20 whitespace-nowrap">
                        심사 완료
                      </span>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] px-3 py-2.5">
                  <p className="text-gray-400 text-[10px] mb-1">메시지</p>
                  <p className="text-gray-300 text-xs whitespace-pre-wrap leading-relaxed">{item.message}</p>
                </div>

                {/* Carriers */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {carriers.map((carrier) => {
                    const style = CARRIER_STYLES[carrier] || CARRIER_STYLES.SKT;
                    const review = reviewMap.get(carrier);
                    const key = `${item.id}-${carrier}`;
                    const isReviewing = reviewing[key];
                    // Find the delivery status from results
                    const resultForCarrier = (item.results || []).find((r: any) => r.carrier === carrier);
                    const deliveryStatus = resultForCarrier?.delivery_status;
                    const showDeliveryInfo = !review && deliveryStatus === "delivered";

                    return (
                      <div key={carrier} className={`rounded-lg border ${style.border} ${style.bg} px-3 py-2.5 space-y-2`}>
                        <div className="flex items-center justify-between">
                          <span className={`text-xs font-bold ${style.text}`}>{carrier}</span>
                          {review ? (
                            <span
                              className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                review.status === "success"
                                  ? "bg-emerald-500/15 text-emerald-400"
                                  : "bg-rose-500/15 text-rose-400"
                              }`}
                            >
                              {review.status === "success" ? "성공" : "스팸"}
                            </span>
                          ) : showDeliveryInfo ? (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400">
                              배달 확인됨
                            </span>
                          ) : (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">
                              진행중
                            </span>
                          )}
                        </div>

                        {showDeliveryInfo && (
                          <p className="text-[10px] text-sky-400/80 leading-relaxed">
                            MessageBird 배달 확인 · 단말기 수신 대기중
                          </p>
                        )}

                        {!review && (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleReview(item.id, carrier, "success")}
                              disabled={isReviewing}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/20 text-emerald-400 text-[10px] font-medium hover:bg-emerald-500/25 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
                            >
                              {isReviewing ? (
                                <i className="ri-loader-4-line animate-spin text-xs"></i>
                              ) : (
                                <i className="ri-checkbox-circle-line text-xs"></i>
                              )}
                              성공
                            </button>
                            <button
                              onClick={() => handleReview(item.id, carrier, "spam")}
                              disabled={isReviewing}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-rose-500/15 border border-rose-500/20 text-rose-400 text-[10px] font-medium hover:bg-rose-500/25 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
                            >
                              {isReviewing ? (
                                <i className="ri-loader-4-line animate-spin text-xs"></i>
                              ) : (
                                <i className="ri-close-circle-line text-xs"></i>
                              )}
                              스팸
                            </button>
                          </div>
                        )}

                        {review && (
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => handleReview(item.id, carrier, "success")}
                              disabled={isReviewing}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/20 text-emerald-400 text-[10px] font-medium hover:bg-emerald-500/25 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
                            >
                              {isReviewing ? (
                                <i className="ri-loader-4-line animate-spin text-xs"></i>
                              ) : (
                                <i className="ri-checkbox-circle-line text-xs"></i>
                              )}
                              성공
                            </button>
                            <button
                              onClick={() => handleReview(item.id, carrier, "spam")}
                              disabled={isReviewing}
                              className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md bg-rose-500/15 border border-rose-500/20 text-rose-400 text-[10px] font-medium hover:bg-rose-500/25 disabled:opacity-50 cursor-pointer whitespace-nowrap transition-colors"
                            >
                              {isReviewing ? (
                                <i className="ri-loader-4-line animate-spin text-xs"></i>
                              ) : (
                                <i className="ri-close-circle-line text-xs"></i>
                              )}
                              스팸
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 flex items-center gap-3 px-4 py-3 rounded-xl border border-emerald-500/30 bg-[#1a1a2e] text-emerald-400 shadow-lg z-[100] transition-all">
          <i className="ri-checkbox-circle-line text-base"></i>
          <span className="text-sm">{toast}</span>
        </div>
      )}
    </div>
  );
}